import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, UserPlus, Users, Download } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const CITIES = ["Bern", "Basel", "Lausanne"];
const ROLES = ["Gründer", "Leiter", "Leiterin", "Stellvertreter", "Mitglied"];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const emptyForm = (city) => ({ name: "", city, role: "Mitglied", age: "", note: "" });

const fieldClass = "h-11 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60";
const selectClass = "h-11 w-full rounded-xl border border-white/15 bg-anthracite text-cream px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold/60";

const MembersPanel = ({ onLogout }) => {
  const [members, setMembers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // member or null
  const [form, setForm] = useState(emptyForm("Bern"));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/members`, { headers: authHeader() });
      setMembers(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Mitglieder konnten nicht geladen werden.");
      setMembers([]);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const openAdd = (city) => { setEditing(null); setForm(emptyForm(city)); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, city: m.city, role: m.role, age: m.age ?? "", note: m.note ?? "" });
    setDialogOpen(true);
  };
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      city: form.city,
      role: form.role,
      age: form.age === "" ? null : Number(form.age),
      note: form.note.trim() || null,
    };
    try {
      if (editing) {
        const { data } = await axios.put(`${API}/admin/members/${editing.id}`, payload, { headers: authHeader() });
        setMembers((prev) => prev.map((m) => (m.id === editing.id ? data : m)));
        toast.success("Mitglied aktualisiert.");
      } else {
        const { data } = await axios.post(`${API}/admin/members`, payload, { headers: authHeader() });
        setMembers((prev) => [...prev, data]);
        toast.success("Mitglied hinzugefügt.");
      }
      setDialogOpen(false);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    downloadCSV(
      `boe_mitglieder_${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "name", label: "Name" },
        { key: "city", label: "Stadt" },
        { key: "role", label: "Rolle" },
        { key: "age", label: "Alter" },
        { key: "note", label: "Notiz" },
        { key: "created_at", label: "Erstellt", format: (v) => (v ? new Date(v).toLocaleDateString("de-CH") : "") },
      ],
      [...members].sort((a, b) => a.city.localeCompare(b.city) || a.name.localeCompare(b.name)),
    );
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/members/${id}`, { headers: authHeader() });
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Mitglied gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  if (members === null) {
    return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;
  }

  return (
    <div data-testid="members-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
        <div>
          <span className="section-eyebrow">Die Kreise</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Mitglieder-Übersicht</h1>
          <p className="mt-2 text-sm text-cream/60">Leitungen und Mitglieder der drei Kreise – jederzeit pflegbar.</p>
        </div>

        {/* Total counter card (top right) */}
        <div className="flex flex-col items-end gap-3">
          <button
            onClick={exportCSV}
            disabled={members.length === 0}
            data-testid="members-export-csv"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-gold-soft hover:bg-gold hover:text-anthracite transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gold-soft"
            style={{ color: "#E5C76B" }}
          >
            <Download size={14} /> CSV-Export
          </button>
          <div className="rounded-2xl border border-gold/30 bg-white/[0.04] px-7 py-5 min-w-[260px]" data-testid="members-total">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "#C69C22" }}>
            <Users size={15} /> Mitglieder gesamt
          </div>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-cream" data-testid="members-total-count">{members.length}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-cream/55">
            {CITIES.map((c) => (
              <span key={c}>{c}: <span className="text-cream/80 font-semibold">{members.filter((m) => m.city === c).length}</span></span>
            ))}
          </div>
        </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {CITIES.map((city) => {
          const list = members.filter((m) => m.city === city);
          return (
            <div key={city} data-testid={`members-col-${city}`} className="rounded-2xl border border-gold/15 bg-white/[0.02] p-5">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-gold/15">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-xl font-semibold text-cream">{city}</h2>
                  <span className="text-xs text-cream/45">{list.length}</span>
                </div>
                <button
                  onClick={() => openAdd(city)}
                  data-testid={`members-add-${city}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-gold/40 px-3 py-1.5 text-gold-soft hover:bg-gold hover:text-anthracite transition-colors"
                  style={{ color: "#E5C76B" }}
                >
                  <Plus size={14} /> Hinzufügen
                </button>
              </div>

              {list.length === 0 ? (
                <p className="text-sm text-cream/40 py-6 text-center">Noch keine Einträge.</p>
              ) : (
                <div className="space-y-3">
                  {list.map((m) => (
                    <div key={m.id} data-testid={`member-card-${m.id}`} className="group rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-gold/30 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#C69C22" }}>{m.role}</span>
                          <p className="font-semibold text-cream mt-0.5">{m.name}</p>
                        </div>
                        <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`} aria-label="Bearbeiten" className="text-cream/60 hover:text-gold-soft transition-colors"><Pencil size={15} /></button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`member-delete-${m.id}`} disabled={deletingId === m.id} aria-label="Löschen" className="text-cream/50 hover:text-red-400 transition-colors disabled:opacity-50">
                                {deletingId === m.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-cream">Mitglied löschen?</AlertDialogTitle>
                                <AlertDialogDescription className="text-cream/60">
                                  <span className="text-cream/90 font-medium">{m.name}</span> wirklich aus {m.city} entfernen?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                                <AlertDialogAction data-testid={`member-delete-confirm-${m.id}`} onClick={() => remove(m.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-cream/55">
                        {m.age != null && <span>{m.age} Jahre</span>}
                        {m.note && <span className="text-cream/45">· {m.note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-anthracite-soft border border-gold/20 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-2">
              <UserPlus size={18} style={{ color: "#E5C76B" }} />
              {editing ? "Mitglied bearbeiten" : "Mitglied hinzufügen"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="member-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Name</span>
              <Input value={form.name} onChange={update("name")} required data-testid="member-form-name" className={fieldClass} />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Stadt</span>
                <select value={form.city} onChange={update("city")} data-testid="member-form-city" className={selectClass}>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Rolle</span>
                <select value={form.role} onChange={update("role")} data-testid="member-form-role" className={selectClass}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Alter (optional)</span>
              <Input type="number" min="1" max="120" value={form.age} onChange={update("age")} data-testid="member-form-age" className={fieldClass} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Notiz (optional)</span>
              <Textarea value={form.note} onChange={update("note")} rows={2} data-testid="member-form-note" className="rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60 resize-none" />
            </label>
            <DialogFooter className="mt-2">
              <button type="submit" disabled={saving} data-testid="member-form-submit" className="btn-primary w-full disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Speichern…</> : editing ? "Änderungen speichern" : "Hinzufügen"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersPanel;
